// src/Component/Route/AuthCallback.js
import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Auth } from "aws-amplify";
import { saveAuditTrail } from "../../libs/API";

export default function AuthCallback({ setUserPermissions }) {
  const history = useHistory();
  const ran = useRef(false);

  async function waitForCognito(maxMs = 15000) {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      try {
        await Auth.currentSession();
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        return;
      } catch {
        await new Promise((r) => setTimeout(r, 150));
      }
    }
    throw new Error("Cognito session not ready");
  }

  function parseClientIdHint() {
    const qs = new URLSearchParams(window.location.search);

    // allow explicit override: /auth/callback?cid=123
    const cid = qs.get("cid");
    if (cid && !isNaN(parseInt(cid, 10))) return parseInt(cid, 10) || 0;

    // parse OAuth state (we sent customState in AzurePreAuthPage)
    const state = qs.get("state") || "";
    const candidates = [state];
    try {
      candidates.push(decodeURIComponent(state));
    } catch {}
    try {
      candidates.push(atob(state));
    } catch {}
    for (const c of candidates) {
      try {
        const obj = JSON.parse(c);
        if (obj && obj.clientId) return parseInt(obj.clientId, 10) || 0;
      } catch {}
    }

    // fallback to storage
    try {
      const raw =
        sessionStorage.getItem("pendingClientId") ||
        localStorage.getItem("pendingClientId");
      if (raw) return parseInt(raw, 10) || 0;
    } catch {}

    return 0;
  }

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // one-shot guard; cleared on success/error
    if (sessionStorage.getItem("oauth_once") === "1") return;
    sessionStorage.setItem("oauth_once", "1");

    (async () => {
      try {
        const clientIdHint = parseClientIdHint();

        // optional IP (best-effort)
        let ip = "";
        try {
          const resp = await fetch("https://api.ipify.org?format=json");
          const j = await resp.json();
          ip = j?.ip || "";
        } catch {}

        // make sure tokens are ready (Incognito-safe)
        await waitForCognito();

        // set roles/flags in App (expects ip + clientId)
        await setUserPermissions(ip, clientIdHint);

        // audit (best-effort)
        try {
          const user = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
          const email = user?.attributes?.email || user?.username || "";
          if (email) {
            await saveAuditTrail({
              emailAddress: email,
              ipAddress: ip,
              eventLog: "LogIn",
            });
          }
        } catch {}

        // cleanup and go
        try {
          sessionStorage.removeItem("oauth_once");
          sessionStorage.removeItem("pendingClientId");
          localStorage.removeItem("pendingClientId");
        } catch {}
        history.replace("/browse");
      } catch (err) {
        console.error("OAuth callback error:", err);
        try {
          sessionStorage.removeItem("oauth_once");
        } catch {}
        history.replace("/");
      }
    })();
  }, [history, setUserPermissions]);

  return <div>Signing you in…</div>;
}
